import { computed, type Ref } from 'vue';
import { useQueries } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { PageSchema, PartnerBadge } from '@shopexperts/shared';
import { normalizeEntityId } from '../utils/metafieldValues';
import {
  badgeToPublicReference,
  categoryToPublicReference,
  subcategoryToPublicReference,
} from '../utils/entityPublicReference';
import { useCategories } from './useCategories';

/**
 * Resolves the entity references (partner badge, service category and
 * subcategory) held in the metafields draft into the public-reference shape
 * the API serves on published pages, so the live preview receives the same
 * metafield values as production.
 */
export function usePreviewMetafieldEntities(
  schema: Ref<PageSchema | null | undefined>,
  draft: Ref<Record<string, any>>,
) {
  const api = useApi();
  const { categoriesWithSubcategories, categoryById } = useCategories();

  const badgeIds = computed(() => {
    const ids = new Set<number>();
    for (const field of schema.value?.fields || []) {
      if (field.type !== 'partner_badge') continue;
      const id = normalizeEntityId(draft.value[field.key]);
      if (id) ids.add(id);
    }
    return [...ids].sort((a, b) => a - b);
  });

  const badgeQueries = useQueries({
    queries: computed(() => badgeIds.value.map((id) => ({
      queryKey: ['admin', 'badge', id],
      queryFn: async () => api.admin.badges.getBadge({ params: { badge: id } }),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    }))),
  });

  const badgesById = computed(() => {
    const map: Record<number, PartnerBadge> = {};
    for (const query of badgeQueries.value) {
      const badge = query.data as PartnerBadge | undefined;
      if (badge?.id) map[badge.id] = badge;
    }
    return map;
  });

  const entities = computed(() => {
    const result: Record<string, unknown> = {};
    for (const field of schema.value?.fields || []) {
      const id = normalizeEntityId(draft.value[field.key]);
      if (!id) continue;
      if (field.type === 'partner_badge') {
        const badge = badgesById.value[id];
        if (badge) result[field.key] = badgeToPublicReference(badge);
      } else if (field.type === 'service_category') {
        const category = categoryById.value[id];
        if (category) result[field.key] = categoryToPublicReference(category);
      } else if (field.type === 'service_subcategory') {
        for (const category of categoriesWithSubcategories.value) {
          const subcategory = category.subcategories.find((entry) => entry.id === id);
          if (subcategory) {
            result[field.key] = subcategoryToPublicReference(subcategory, category);
            break;
          }
        }
      }
    }
    return result;
  });

  return { entities };
}

export default usePreviewMetafieldEntities;
